import { useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'

import { Links } from 'components/routes/paths'
import { addQueryString } from 'helpers/url'

import { MasterAccountRef } from 'models/master-account'
import { TenantRef } from 'models/tenant'

interface NavigateToChargersParams {
  ocppStatus?: string
  serviceStatus?: string
  connectivityStatus?: string
  country?: string
  province?: string
}

interface ChargerNavigationHelpers {
  goToOnlineChargers: () => void
  goToOfflineChargers: () => void
  goToTroubleshootingChargers: () => void
  goToUnderRepairChargers: () => void
}

export const useNavigateToChargers = (
  initialMasterAccounts?: MasterAccountRef[],
  initialTenants?: TenantRef[],
): { navigateToChargers: (params: NavigateToChargersParams) => void } & ChargerNavigationHelpers => {
  const history = useHistory()

  const navigateToChargers = useCallback(
    ({
      ocppStatus,
      serviceStatus,
      connectivityStatus,
      country,
      province,
    }: NavigateToChargersParams) => {
      const queryParams: { [key: string]: string | number | string[] } = {}

      // Add status parameters
      if (ocppStatus) queryParams['ocppStatus'] = ocppStatus
      if (serviceStatus) queryParams['serviceStatus'] = serviceStatus
      if (connectivityStatus) queryParams['connectivityStatus'] = connectivityStatus

      // Add MasterAccountRef as a JSON object
      if (initialMasterAccounts && initialMasterAccounts.length > 0) {
        queryParams['masterAccounts'] = encodeURIComponent(JSON.stringify(initialMasterAccounts))
      }

      // Add TenantRef array as a JSON string
      if (initialTenants && initialTenants.length > 0) {
        queryParams['tenants'] = encodeURIComponent(JSON.stringify(initialTenants))
      }

      if (country) queryParams['country'] = country
      if (province) queryParams['province'] = province

      // Navigate to the chargers page with the query string
      history.push(addQueryString(Links.chargers(), queryParams))
    },
    [history, initialMasterAccounts, initialTenants],
  )

  // Memoize common navigation functions to avoid recreating them on every render
  const navigationHelpers = useMemo<ChargerNavigationHelpers>(
    () => ({
      goToOnlineChargers: () => navigateToChargers({ connectivityStatus: 'online', serviceStatus: 'active' }),
      goToOfflineChargers: () => navigateToChargers({ connectivityStatus: 'offline', serviceStatus: 'active' }),
      goToTroubleshootingChargers: () => navigateToChargers({ serviceStatus: 'troubleshooting' }),
      goToUnderRepairChargers: () => navigateToChargers({ serviceStatus: 'underRepair' }),
    }),
    [navigateToChargers],
  )

  return {
    navigateToChargers,
    ...navigationHelpers,
  }
}